/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from "lodash/get";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { addTargetBlankToExtLinks } from "../../../../../utils/add-target-blank-to-extlinks.js";
import { isEmpty, selectedStoryTemplate, selectedStoryTemplateSettings } from "../../../../utils.js";
import { AdComponent, WidgetComp } from "../../../DetectComponent";
import { checkIfStoryPublic } from "../../../sso-components/sso-utils.js";
import { loadRelatedStories } from "../../../story-page-components/related-stories-helper.js";
import PaywallStory from "../../paywall-story.js";
import PublicStory from "../../public-story.js";

const TextStoryBase = props => {
  const isStoryPublic = checkIfStoryPublic(props.story);
  const [relatedStories, setRelatedStories] = useState([]);

  const storyWrapperElement = useRef(null);

  useEffect(() => {
    // check if asside collection stories are provided. if yes then don't load related stories
    const asideCollectionData = get(props, ["templateConfig", "asideCollection", "data"], []);
    !asideCollectionData.length &&
      loadRelatedStories(props.story, props.config).then(relatedStories => setRelatedStories(relatedStories));
    addTargetBlankToExtLinks(storyWrapperElement.current);
  }, []);

  const storyTemplate = selectedStoryTemplate(props.storyPageType, props.story, props.config, props.storyStoreField);
  const pbStoryConfig = get(props.config, ["pagebuilder-config", props.storyStoreField, props.storyPageType], {});
  const showAllCards = -1;
  const color = get(pbStoryConfig, ["settings", "theme"], "");
  const asideSlots = get(pbStoryConfig, ["asideSlots"]);

  const templateConfig = {
    theme: "",
    verticalShare: "",
    shareIconType: "plain-svg",
    templateType: storyTemplate,
    noOfVisibleCards: showAllCards,
    showSectionTag: true,
    publishedDetails: {
      enablePublishedTime: true,
      enableUpdatedTime: false,
      showReadTime: false
    },
    asideCollection: {
      data: relatedStories,
      slots: asideSlots || [
        {
          type: "ad",
          layout: "Story-Mrec",
          layoutLabel: "Story-Mrec",
          config: {
            targetingId: "story-mrec-aside-collections"
          }
        },
        {
          type: "collection"
        }
      ],
      config: {
        title: "Related Stories",
        theme: color,
        collectionNameBorderColor: "#000",
        showAuthor: true,
        showTime: true
      }
    },
    authorCard: {
      template: "",
      opts: {
        hideBio: true,
        hideImage: false
      }
    }
  };

  const storyTemplateSettings = selectedStoryTemplateSettings(props.storyPageType, props.config, props.storyStoreField);
  const updatedConfig = Object.assign({}, templateConfig, props.templateConfig, storyTemplateSettings);
  const textConfig = !isEmpty(pbStoryConfig) ? updatedConfig : templateConfig;
  const bgColor = updatedConfig.theme || "";
  const storyElementsConfig = get(props.config, ["pagebuilder-config", "story", "globalStoryElements"], {});

  return (
    <div style={{ backgroundColor: bgColor }}>
      <div ref={storyWrapperElement}>
        {isStoryPublic ? (
          <PublicStory
            story={props.story}
            config={props.config}
            templateConfig={textConfig}
            preview={props.preview}
            storyElementsConfig={storyElementsConfig}
            adComponent={props.adComponent || AdComponent}
            widgetComp={props.widgetComp || WidgetComp}
            storyBaseType={props.storyBaseType}
          />
        ) : (
          <PaywallStory
            {...props}
            templateConfig={textConfig}
            storyElementsConfig={storyElementsConfig}
            adComponent={props.adComponent || AdComponent}
            widgetComp={props.widgetComp || WidgetComp}
            storyBaseType={props.storyBaseType}
          />
        )}
      </div>
    </div>
  );
};

TextStoryBase.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  templateConfig: PropTypes.object,
  storyBaseType: PropTypes.string,
  storyStoreField: PropTypes.string,
  storyPageType: PropTypes.string,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

TextStoryBase.defaultProps = {
  templateConfig: {}
};

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const TextStory = connect(mapStateToProps)(TextStoryBase);

export default TextStory;
